// src/pages/admin/ManageDeposits.js
import React, { useState, useEffect } from 'react';
import { database, ref, get, update, set, push } from '../../firebase';
import { 
  CheckCircle, XCircle, Eye, DollarSign, Gift, Clock, 
  Filter, Search, Download, User, Phone, Mail, 
  ChevronDown, ChevronUp, Package, Wallet, Users,
  TrendingUp, Award, Calendar, CreditCard,
  ShieldCheck
} from 'lucide-react';

const ManageDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('approved');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [stats, setStats] = useState({
    totalPending: 0,
    totalApproved: 0,
    totalRejected: 0,
    totalAmount: 0,
    totalBonus: 0,
    totalCredited: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterDeposits();
  }, [searchTerm, statusFilter, typeFilter, deposits]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDeposits(),
        fetchUsers(),
        fetchPackages()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeposits = async () => {
    try {
      const depositsRef = ref(database, 'Deposits');
      const snapshot = await get(depositsRef);
      
      if (snapshot.exists()) {
        const depositsData = snapshot.val();
        const depositsArray = Object.keys(depositsData).map(key => ({
          id: key,
          ...depositsData[key]
        }));
        
        // Sort by date (newest first)
        depositsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDeposits(depositsArray);
        
        // Calculate stats
        calculateStats(depositsArray);
      } else {
        setDeposits([]);
      }
    } catch (error) {
      console.error('Error fetching deposits:', error);
      setDeposits([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.keys(usersData).map(key => ({
          id: key,
          ...usersData[key]
        }));
        setUsers(usersArray);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchPackages = async () => {
    try {
      const packagesRef = ref(database, 'Packages');
      const snapshot = await get(packagesRef);
     
      if (snapshot.exists()) {
        const packagesData = snapshot.val();
        const packagesArray = Object.keys(packagesData).map(key => ({
          id: key,
          ...packagesData[key]
        }));
        setPackages(packagesArray);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
    }
  };

  const calculateStats = (depositsArray) => {
    const stats = {
      totalPending: 0,
      totalApproved: 0,
      totalRejected: 0,
      totalAmount: 0,
      totalBonus: 0,
      totalCredited: 0
    };
    
    depositsArray.forEach(deposit => {
      if (deposit.status === 'pending') stats.totalPending++;
      else if (deposit.status === 'approved') stats.totalApproved++;
      else if (deposit.status === 'rejected') stats.totalRejected++;
      
      const amount = parseFloat(deposit.amount || 0);
      const bonus = parseFloat(deposit.welcomeBonus || 0);
      
      stats.totalAmount += amount;
      stats.totalBonus += bonus;
      stats.totalCredited += amount + bonus;
    });
    
    setStats(stats);
  };

  const filterDeposits = () => {
    let filtered = [...deposits];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(d => {
        const amount = parseFloat(d.amount || 0);
        if (typeFilter === 'small') return amount >= 10 && amount <= 249;
        if (typeFilter === 'medium') return amount >= 250 && amount <= 2499;
        if (typeFilter === 'large') return amount >= 2500 && amount <= 9999;
        if (typeFilter === 'xlarge') return amount >= 10000;
        return true;
      });
    }
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        (d.userId && d.userId.toLowerCase().includes(term)) ||
        (d.userName && d.userName.toLowerCase().includes(term)) ||
        (d.depositId && d.depositId.toLowerCase().includes(term)) ||
        (d.paymentMethod && d.paymentMethod.toLowerCase().includes(term)) ||
        (d.paymentMethodName && d.paymentMethodName.toLowerCase().includes(term))
      );
    }
    
    setFilteredDeposits(filtered);
  };

  const getUserDetails = async (userId) => {
    if (userDetails[userId]) return userDetails[userId];
    
    try {
      const userRef = ref(database, `users/${userId}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const details = {
          id: userId,
          name: data.name || data.userName || 'Unknown',
          email: data.email || data.userId || 'N/A',
          mobile: data.mobile || data.phone || 'N/A',
          country: data.country || 'Unknown',
          sponsorId: data.sponsorId || 'N/A',
          uplineSponsorId: data.uplineSponsorId || 'N/A',
          balance: parseFloat(data.balance) || 0,
          fundBalance: parseFloat(data.fundBalance) || 0,
          aurumBalance: parseFloat(data.aurumBalance) || 0,
          totalEarnings: parseFloat(data.totalEarnings) || 0,
          totalDeposited: parseFloat(data.depositTotalAmount) || 0,
          registrationDate: data.registrationDate || data.createdAt || 'N/A',
          lastLogin: data.lastLogin || 'N/A',
          status: data.status || 'active',
          packages: data.packages || [],
          referrals: data.referrals || [],
          memberId: data.memberId || userId
        };
        
        setUserDetails(prev => ({ ...prev, [userId]: details }));
        return details;
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
    
    return null;
  };

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      await getUserDetails(userId);
    }
  };

  const updateDepositStatus = async (depositId, status, adminNote = '') => {
    try {
      const depositRef = ref(database, `Deposits/${depositId}`);
      const depositSnapshot = await get(depositRef);
      
      if (!depositSnapshot.exists()) {
        alert('Deposit not found');
        return;
      }
      
      const depositData = depositSnapshot.val();
      
      if (status === 'approved') {
        // Check if already approved
        if (depositData.status === 'approved') {
          alert('Deposit is already approved!');
          return;
        }
        
        // Save deposit amount to user's database and update fundBalance
        const saveResult = await saveDepositAmountToUser(depositData);
        
        if (!saveResult.success) {
          alert(`Error: ${saveResult.error}`);
          return;
        }
        
        // Add funds to user
        const addFundsResult = await addFundsToUser(depositData);
        
        if (!addFundsResult.success) {
          alert(`Error: ${addFundsResult.error}`);
          return;
        }
        
        // Update deposit record
        await update(depositRef, {
          status: 'approved',
          approvedAt: new Date().toISOString(),
          approvedBy: 'admin',
          adminNote: adminNote,
          creditedAmount: parseFloat(depositData.amount) + parseFloat(depositData.welcomeBonus || 0),
          creditedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        // Create transaction record
        await createTransactionRecord(depositData, 'completed');
        
        alert(`✅ Deposit approved successfully!\n\n` +
              `User: ${depositData.userName}\n` +
              `Deposit: $${depositData.amount}\n` +
              `Bonus: $${depositData.welcomeBonus || 0}\n` +
              `Total Credited: $${(parseFloat(depositData.amount) + parseFloat(depositData.welcomeBonus || 0)).toFixed(2)}`);
        
      } else if (status === 'rejected') {
        // Check if already approved
        if (depositData.status === 'approved') {
          alert('Cannot reject an already approved deposit!');
          return;
        }
        
        await update(depositRef, {
          status: 'rejected',
          rejectedAt: new Date().toISOString(),
          rejectedBy: 'admin',
          adminNote: adminNote,
          updatedAt: new Date().toISOString()
        });
        
        // Create failed transaction
        await createTransactionRecord(depositData, 'rejected');
        
        alert('❌ Deposit request rejected');
      }
      
      // Refresh data
      await fetchAllData();
      setSelectedDeposit(null);
      
    } catch (error) {
      console.error('Error updating deposit status:', error);
      alert('Error updating deposit status: ' + error.message);
    }
  };

  // Save deposit amount to user's database
  const saveDepositAmountToUser = async (depositData) => {
    try {
      const userRef = ref(database, `users/${depositData.userId}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const depositAmount = parseFloat(depositData.amount);
        const welcomeBonus = parseFloat(depositData.welcomeBonus || 0);
        const totalAmount = depositAmount + welcomeBonus;
        
        // Calculate current balances
        const currentFundBalance = parseFloat(userData.fundBalance) || 0;
        const currentAurumBalance = parseFloat(userData.aurumBalance) || 0;
        const currentDepositTotal = parseFloat(userData.depositTotalAmount) || 0;
        const currentTotalBalance = parseFloat(userData.balance) || 0;
        
        // Calculate new balances
        let newFundBalance = currentFundBalance;
        let newAurumBalance = currentAurumBalance;
        let newTotalBalance = currentTotalBalance + totalAmount;
        let newDepositTotal = currentDepositTotal + depositAmount; // Only deposit amount, not bonus
        
        // Add to appropriate wallet
        if (depositData.walletType === 'fund') {
          newFundBalance += totalAmount;
        } else {
          newAurumBalance += totalAmount;
        }
        
        // Update user data
        const updates = {
          // Update fund or aurum balance based on wallet type
          ...(depositData.walletType === 'fund' 
            ? { fundBalance: newFundBalance } 
            : { aurumBalance: newAurumBalance }),
          
          // Update total deposit amount (deposit only, no bonus)
          depositTotalAmount: newDepositTotal.toFixed(2),
          
          // Update total balance
          balance: newTotalBalance.toFixed(2),
          
          // Update last deposit info
          lastDepositDate: new Date().toISOString(),
          lastDepositAmount: depositAmount,
          depositCount: (parseInt(userData.depositCount) || 0) + 1,
          
          // Add welcome bonus to total earnings
          totalEarnings: (parseFloat(userData.totalEarnings) || 0) + welcomeBonus,
          
          // Add welcome bonus to total bonus received
          totalBonusReceived: (parseFloat(userData.totalBonusReceived) || 0) + welcomeBonus,
          
          updatedAt: new Date().toISOString()
        };
        
        await update(userRef, updates);
        
        console.log('✅ User balances updated:', {
          userId: depositData.userId,
          depositAmount,
          welcomeBonus,
          totalAmount,
          newFundBalance,
          newAurumBalance,
          newDepositTotal,
          walletType: depositData.walletType
        });
        
        return { 
          success: true, 
          data: { 
            newFundBalance, 
            newAurumBalance, 
            newDepositTotal,
            newTotalBalance 
          } 
        };
      }
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Error saving deposit amount to user:', error);
      return { success: false, error: error.message };
    }
  };

  // Add funds to user (for welcome bonus tracking)
  const addFundsToUser = async (depositData) => {
    try {
      const depositAmount = parseFloat(depositData.amount);
      const welcomeBonus = parseFloat(depositData.welcomeBonus || 0);
      
      if (welcomeBonus > 0) {
        // Create income record for welcome bonus
        const incomeRef = push(ref(database, 'Incomes'));
        await set(incomeRef, {
          incomeId: incomeRef.key,
          userId: depositData.userId,
          userName: depositData.userName,
          type: 'welcome_bonus',
          amount: welcomeBonus,
          percentage: ((welcomeBonus / depositAmount) * 100).toFixed(1),
          date: new Date().toISOString(),
          depositId: depositData.depositId,
          depositAmount: depositAmount,
          walletType: depositData.walletType,
          status: 'credited',
          description: `Welcome bonus for $${depositAmount} deposit`,
          createdAt: new Date().toISOString()
        });
        
        console.log('✅ Welcome bonus income record created');
      }
      
      // Create deposit record in user's history
      const depositRecordRef = push(ref(database, 'Transactions'));
      await set(depositRecordRef, {
        transactionId: depositRecordRef.key,
        userId: depositData.userId,
        userName: depositData.userName,
        type: 'deposit',
        depositAmount: depositAmount,
        bonusAmount: welcomeBonus,
        totalAmount: depositAmount + welcomeBonus,
        walletType: depositData.walletType,
        depositId: depositData.depositId,
        paymentMethod: depositData.paymentMethod || depositData.paymentMethodName,
        status: 'completed',
        date: new Date().toISOString(),
        description: `Deposit of $${depositAmount.toFixed(2)} with $${welcomeBonus.toFixed(2)} bonus`,
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Deposit transaction record created');
      
      return { success: true };
    } catch (error) {
      console.error('Error adding funds:', error);
      return { success: false, error: error.message };
    }
  };

  const createTransactionRecord = async (depositData, status) => {
    try {
      const transactionRef = push(ref(database, 'Transactions'));
      await set(transactionRef, {
        transactionId: transactionRef.key,
        userId: depositData.userId,
        userName: depositData.userName,
        type: 'deposit',
        amount: parseFloat(depositData.amount),
        bonusAmount: parseFloat(depositData.welcomeBonus || 0),
        totalAmount: parseFloat(depositData.amount) + parseFloat(depositData.welcomeBonus || 0),
        walletType: depositData.walletType,
        depositId: depositData.depositId,
        paymentMethod: depositData.paymentMethod || depositData.paymentMethodName,
        status: status,
        date: new Date().toISOString(),
        description: `Deposit ${status}: $${depositData.amount} + $${depositData.welcomeBonus || 0} bonus`,
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Transaction record created');
    } catch (error) {
      console.error('Error creating transaction:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return { 
        bg: 'bg-yellow-500/20', 
        text: 'text-yellow-400', 
        border: 'border-yellow-500/30',
        icon: <Clock className="h-4 w-4" />,
        label: 'Pending' 
      };
      case 'approved': return { 
        bg: 'bg-green-500/20', 
        text: 'text-green-400',
        border: 'border-green-500/30',
        icon: <CheckCircle className="h-4 w-4" />,
        label: 'Auto-Approved' 
      };
      case 'rejected': return { 
        bg: 'bg-red-500/20', 
        text: 'text-red-400',
        border: 'border-red-500/30',
        icon: <XCircle className="h-4 w-4" />,
        label: 'Rejected' 
      };
      default: return { 
        bg: 'bg-gray-500/20', 
        text: 'text-gray-400',
        border: 'border-gray-500/30',
        icon: null,
        label: status 
      };
    }
  };

  const getPackageName = (amount) => {
    const amt = parseFloat(amount || 0);
    if (amt >= 50000) return 'Package 5';
    if (amt >= 10000) return 'Package 4';
    if (amt >= 2500) return 'Package 3';
    if (amt >= 250) return 'Package 2';
    if (amt >= 10) return 'Package 1';
    return 'Custom';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const exportToCSV = () => {
    const headers = ['Deposit ID', 'User ID', 'User Name', 'Amount', 'Bonus', 'Total', 'Payment Method', 'Wallet', 'Status', 'Date', 'Package', 'Approved At'];
    const csvData = filteredDeposits.map(deposit => [
      deposit.depositId,
      deposit.userId,
      deposit.userName,
      `$${parseFloat(deposit.amount).toFixed(2)}`,
      `$${parseFloat(deposit.welcomeBonus || 0).toFixed(2)}`,
      `$${(parseFloat(deposit.amount) + parseFloat(deposit.welcomeBonus || 0)).toFixed(2)}`,
      deposit.paymentMethodName || deposit.paymentMethod,
      deposit.walletType === 'fund' ? 'Fund Wallet' : 'Aurum Wallet',
      deposit.status,
      formatDateTime(deposit.createdAt),
      getPackageName(deposit.amount),
      deposit.approvedAt ? formatDateTime(deposit.approvedAt) : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deposits-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Auto-approve all pending deposits
  const autoApproveAllPending = async () => {
    const pendingDeposits = deposits.filter(d => d.status === 'pending');
    
    if (pendingDeposits.length === 0) {
      alert('No pending deposits to approve');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to auto-approve ${pendingDeposits.length} pending deposits?`)) {
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const deposit of pendingDeposits) {
      try {
        await updateDepositStatus(deposit.id, 'approved', 'Auto-approved by admin');
        successCount++;
      } catch (error) {
        console.error(`Error approving deposit ${deposit.id}:`, error);
        errorCount++;
      }
    }
    
    setLoading(false);
    alert(`✅ Auto-approval completed!\n\n` +
          `Successfully approved: ${successCount}\n` +
          `Failed: ${errorCount}`);
    
    // Refresh data
    await fetchAllData();
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-20 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-500"></div>
          <span className="ml-4 text-gold-300">Loading deposits...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-20 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gold-100">💰 Manage Deposits</h1>
        <button
          onClick={autoApproveAllPending}
          disabled={stats.totalPending === 0}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
            stats.totalPending === 0 
              ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
          }`}
        >
          <ShieldCheck className="h-5 w-5" />
          Auto-Approve All Pending ({stats.totalPending})
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Total Approved</div>
              <div className="text-3xl font-bold text-green-400">{stats.totalApproved}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Pending Deposits</div>
              <div className="text-3xl font-bold text-yellow-400">{stats.totalPending}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Total Amount</div>
              <div className="text-3xl font-bold text-gold-100">${stats.totalAmount.toFixed(2)}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Total Bonus</div>
              <div className="text-3xl font-bold text-green-400">${stats.totalBonus.toFixed(2)}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Gift className="h-6 w-6 text-emerald-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Total Credited</div>
              <div className="text-3xl font-bold text-green-400">${stats.totalCredited.toFixed(2)}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gold-400 text-sm mb-2">Rejected</div>
              <div className="text-3xl font-bold text-red-400">{stats.totalRejected}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by user ID, name, deposit ID..."
              className="w-full pl-10 pr-4 py-3 bg-gold-900/50 border border-gold-600/50 rounded-xl text-gold-100 placeholder-gold-600 focus:outline-none focus:border-gold-500"
            />
            <Search className="absolute left-3 top-3.5 h-5 w-5 text-gold-500" />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-gold-900/50 border border-gold-600/50 rounded-xl text-gold-100 focus:outline-none focus:border-gold-500"
          >
            <option value="all">All Status</option>
            <option value="approved">Auto-Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-3 bg-gold-900/50 border border-gold-600/50 rounded-xl text-gold-100 focus:outline-none focus:border-gold-500"
          >
            <option value="all">All Packages</option>
            <option value="small">Package 1 ($10-$249)</option>
            <option value="medium">Package 2 ($250-$2,499)</option>
            <option value="large">Package 3 ($2,500-$9,999)</option>
            <option value="xlarge">Package 4+ ($10,000+)</option>
          </select>
          
          <button
            onClick={exportToCSV}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all"
          >
            <Download className="h-5 w-5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Deposits List with Dynamic User Dropdown */}
      <div className="bg-gold-900/30 border border-gold-600/50 rounded-2xl overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-gold-900/50">
              <tr>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-48">Deposit Info</th>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-64">User Details</th>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-40">Amount</th>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-40">Payment Info</th>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-32">Status</th>
                <th className="py-4 px-4 text-left text-gold-300 font-semibold w-48">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gold-800/50">
              {filteredDeposits.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-16 text-center">
                    <div className="text-gold-500 text-4xl mb-4">💸</div>
                    <h3 className="text-xl font-semibold text-gold-200 mb-2">No Deposits Found</h3>
                    <p className="text-gold-400">
                      {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                        ? 'No deposits match your filters. Try changing search criteria.'
                        : 'No deposit requests found.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredDeposits.map((deposit) => {
                  const status = getStatusBadge(deposit.status);
                  const userDetail = userDetails[deposit.userId];
                  const isExpanded = expandedUser === deposit.userId;
                  const packageName = getPackageName(deposit.amount);
                  
                  return (
                    <React.Fragment key={deposit.id}>
                      {/* Main Row */}
                      <tr className="hover:bg-gold-900/20 transition-colors">
                        {/* Deposit Info */}
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            <div className="font-mono text-sm text-gold-300">#{deposit.depositId}</div>
                            <div className="text-xs text-gold-500">{formatDateTime(deposit.createdAt)}</div>
                            <div className="flex items-center gap-1 text-xs">
                              <Package className="h-3 w-3 text-gold-400" />
                              <span className="text-gold-400">{packageName}</span>
                            </div>
                          </div>
                        </td>
                        
                        {/* User Details with Dropdown */}
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-between group cursor-pointer" onClick={() => toggleUserDetails(deposit.userId)}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
                                <User className="h-5 w-5 text-gold-400" />
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gold-100 truncate">{deposit.userName}</div>
                                <div className="text-gold-500 text-sm truncate">ID: {deposit.userId}</div>
                                {userDetail && (
                                  <div className="text-xs text-gold-600 mt-1">
                                    Balance: ${userDetail.balance?.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <button className="text-gold-500 hover:text-gold-300">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                        
                        {/* Amount */}
                        <td className="py-4 px-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-gold-400" />
                              <span className="text-xl font-bold text-gold-100">${parseFloat(deposit.amount).toFixed(2)}</span>
                            </div>
                            {parseFloat(deposit.welcomeBonus || 0) > 0 && (
                              <div className="flex items-center gap-1">
                                <Gift className="h-3 w-3 text-green-400" />
                                <span className="text-sm text-green-400">
                                  +${parseFloat(deposit.welcomeBonus || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="text-xs text-gold-500">
                              Total: ${(parseFloat(deposit.amount) + parseFloat(deposit.welcomeBonus || 0)).toFixed(2)}
                            </div>
                          </div>
                        </td>
                        
                        {/* Payment Info */}
                        <td className="py-4 px-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-blue-400" />
                              <span className="text-sm text-gold-300">{deposit.paymentMethodName || deposit.paymentMethod}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Wallet className="h-4 w-4 text-gold-400" />
                              <span className="text-sm text-gold-400">
                                {deposit.walletType === 'fund' ? 'Fund Wallet' : 'Aurum Wallet'}
                              </span>
                            </div>
                          </div>
                        </td>
                        
                        {/* Status */}
                        <td className="py-4 px-4">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${status.bg} ${status.text} ${status.border}`}>
                            {status.icon}
                            <span className="font-medium">{status.label}</span>
                            {deposit.approvedAt && deposit.status === 'approved' && (
                              <span className="text-xs text-gold-500">
                                {formatDate(deposit.approvedAt)}
                              </span>
                            )}
                          </div>
                        </td>
                        
                        {/* Actions */}
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            {deposit.status === 'pending' ? (
                              <>
                                <button
                                  onClick={() => updateDepositStatus(deposit.id, 'approved', 'Deposit verified and approved')}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-1.5 text-sm"
                                  title="Approve"
                                >
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = prompt('Enter rejection reason:');
                                    if (reason) updateDepositStatus(deposit.id, 'rejected', reason);
                                  }}
                                  className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center gap-1.5 text-sm"
                                  title="Reject"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                  Reject
                                </button>
                              </>
                            ) : deposit.status === 'approved' ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-green-400 text-sm font-medium">Auto-Approved</span>
                                <span className="text-gold-500 text-xs">
                                  {formatDateTime(deposit.approvedAt)}
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1">
                                <span className="text-red-400 text-sm font-medium">Rejected</span>
                                <span className="text-gold-500 text-xs">
                                  {formatDateTime(deposit.rejectedAt)}
                                </span>
                              </div>
                            )}
                            <button
                              onClick={() => setSelectedDeposit(deposit)}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-1.5 text-sm"
                              title="View Details"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded User Details Row */}
                      {isExpanded && (
                        <tr className="bg-gold-900/10">
                          <td colSpan="6" className="p-0">
                            <div className="p-4 border-t border-gold-800/50">
                              {userDetail ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                  {/* User Information */}
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2">
                                      <User className="h-4 w-4" />
                                      User Information
                                    </h4>
                                    <div className="space-y-3">
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Name:</span>
                                        <span className="text-gold-100 font-medium">{userDetail.name}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Mobile:</span>
                                        <span className="text-gold-100">{userDetail.mobile}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Country:</span>
                                        <span className="text-gold-100">{userDetail.country}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Sponsor ID:</span>
                                        <span className="text-gold-100">{userDetail.sponsorId}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Registered:</span>
                                        <span className="text-gold-100">{formatDate(userDetail.registrationDate)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Financial Information */}
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2">
                                      <DollarSign className="h-4 w-4" />
                                      Financial Details
                                    </h4>
                                    <div className="space-y-3">
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Total Balance:</span>
                                        <span className="text-xl font-bold text-gold-200">${userDetail.balance?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Fund Wallet:</span>
                                        <span className="text-gold-100">${userDetail.fundBalance?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Aurum Wallet:</span>
                                        <span className="text-gold-100">${userDetail.aurumBalance?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Total Deposited:</span>
                                        <span className="text-blue-400">${userDetail.totalDeposited?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Total Earnings:</span>
                                        <span className="text-green-400">${userDetail.totalEarnings?.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Packages & Activity */}
                                  <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2">
                                      <Package className="h-4 w-4" />
                                      Packages & Activity
                                    </h4>
                                    <div className="space-y-3">
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Active Packages:</span>
                                        <span className="text-gold-100">
                                          {(userDetail.packages?.filter(p => p.status === 'active')?.length || 0)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Total Referrals:</span>
                                        <span className="text-gold-100">{userDetail.referrals?.length || 0}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Last Login:</span>
                                        <span className="text-gold-100">{formatDateTime(userDetail.lastLogin)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gold-500 text-sm">Status:</span>
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          userDetail.status === 'active' 
                                            ? 'bg-green-500/20 text-green-400' 
                                            : 'bg-red-500/20 text-red-400'
                                        }`}>
                                          {userDetail.status}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gold-500 mx-auto"></div>
                                  <p className="text-gold-400 mt-2">Loading user details...</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deposit Details Modal */}
      {selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gold-900/40 to-black border border-gold-600/50 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gold-100">Deposit Details</h3>
              <button onClick={() => setSelectedDeposit(null)} className="text-gold-400 hover:text-gold-200">
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/40 p-4 rounded-xl">
                  <div className="text-gold-400 text-sm">Deposit ID</div>
                  <div className="font-mono text-gold-200 text-sm">{selectedDeposit.depositId}</div>
                </div>
                <div className="bg-black/40 p-4 rounded-xl">
                  <div className="text-gold-400 text-sm">Date & Time</div>
                  <div className="text-gold-200">{formatDateTime(selectedDeposit.createdAt)}</div>
                </div>
                <div className="bg-black/40 p-4 rounded-xl">
                  <div className="text-gold-400 text-sm">Package</div>
                  <div className="text-gold-200 font-bold">{getPackageName(selectedDeposit.amount)}</div>
                </div>
              </div>
              
              {/* Financial Details */}
              <div className="bg-black/40 p-6 rounded-xl">
                <h4 className="text-xl font-bold text-gold-100 mb-4">💰 Financial Details</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gold-300">Deposit Amount:</span>
                    <span className="text-3xl font-bold text-gold-200">${parseFloat(selectedDeposit.amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gold-300">Welcome Bonus ({((parseFloat(selectedDeposit.welcomeBonus || 0) / parseFloat(selectedDeposit.amount)) * 100).toFixed(1)}%):</span>
                    <span className="text-2xl font-bold text-green-400">+${parseFloat(selectedDeposit.welcomeBonus || 0).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gold-600/30 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gold-100">Total to Credit:</span>
                      <span className="text-4xl font-bold text-green-400">
                        ${(parseFloat(selectedDeposit.amount) + parseFloat(selectedDeposit.welcomeBonus || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* User & Payment Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-black/40 p-4 rounded-xl">
                  <h4 className="text-lg font-bold text-gold-100 mb-3">👤 User Information</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gold-300">User ID:</span>
                      <span className="text-gold-100 font-bold">{selectedDeposit.userId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">Name:</span>
                      <span className="text-gold-100">{selectedDeposit.userName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">Wallet:</span>
                      <span className="text-gold-100">
                        {selectedDeposit.walletType === 'fund' ? 'Fund Wallet' : 'Aurum Wallet'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">Previous Deposit Total:</span>
                      <span className="text-gold-100">${parseFloat(selectedDeposit.previousDepositTotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">New Deposit Total:</span>
                      <span className="text-green-100 font-bold">${parseFloat(selectedDeposit.newDepositTotal || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-4 rounded-xl">
                  <h4 className="text-lg font-bold text-gold-100 mb-3">💳 Payment Information</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gold-300">Payment Method:</span>
                      <span className="text-gold-100 font-bold">{selectedDeposit.paymentMethodName || selectedDeposit.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">Package ID:</span>
                      <span className="text-gold-100">{selectedDeposit.packageId || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gold-300">Package Name:</span>
                      <span className="text-gold-100">{selectedDeposit.packageName || getPackageName(selectedDeposit.amount)}</span>
                    </div>
                    {selectedDeposit.adminNote && (
                      <div>
                        <div className="text-gold-300 mb-1">Admin Note:</div>
                        <div className="text-gold-100 text-sm">{selectedDeposit.adminNote}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              {selectedDeposit.status === 'pending' && (
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => {
                      updateDepositStatus(selectedDeposit.id, 'approved', 'Deposit verified');
                      setSelectedDeposit(null);
                    }}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3"
                  >
                    <CheckCircle className="h-6 w-6" />
                    Approve & Credit Funds
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('Enter rejection reason:');
                      if (reason) {
                        updateDepositStatus(selectedDeposit.id, 'rejected', reason);
                        setSelectedDeposit(null);
                      }
                    }}
                    className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white py-4 rounded-xl font-bold text-lg"
                  >
                    Reject Deposit
                  </button>
                </div>
              )}
              
              {/* Status History */}
              {selectedDeposit.status !== 'pending' && (
                <div className="bg-black/40 p-4 rounded-xl">
                  <h4 className="text-lg font-bold text-gold-100 mb-3">Status History</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gold-300">Created:</span>
                      <span className="text-gold-100">{formatDateTime(selectedDeposit.createdAt)}</span>
                    </div>
                    {selectedDeposit.status === 'approved' && selectedDeposit.approvedAt && (
                      <div className="flex justify-between">
                        <span className="text-gold-300">Auto-Approved:</span>
                        <span className="text-green-400">{formatDateTime(selectedDeposit.approvedAt)}</span>
                      </div>
                    )}
                    {selectedDeposit.status === 'rejected' && selectedDeposit.rejectedAt && (
                      <div className="flex justify-between">
                        <span className="text-gold-300">Rejected:</span>
                        <span className="text-red-400">{formatDateTime(selectedDeposit.rejectedAt)}</span>
                      </div>
                    )}
                    {selectedDeposit.approvedBy && (
                      <div className="flex justify-between">
                        <span className="text-gold-300">Approved By:</span>
                        <span className="text-gold-100">{selectedDeposit.approvedBy}</span>
                      </div>
                    )}
                    {selectedDeposit.creditedAmount && (
                      <div className="flex justify-between">
                        <span className="text-gold-300">Credited Amount:</span>
                        <span className="text-green-400 font-bold">${parseFloat(selectedDeposit.creditedAmount).toFixed(2)}</span>
                      </div>
                    )}
                    {selectedDeposit.creditedAt && (
                      <div className="flex justify-between">
                        <span className="text-gold-300">Credited At:</span>
                        <span className="text-gold-100">{formatDateTime(selectedDeposit.creditedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDeposits;